import { createSlice } from "@reduxjs/toolkit"

import moment from "moment"

export const errorsSlice = createSlice({
  name: "errors",
  initialState: [],
  reducers: {
    add: (state, action) => {
      // The same failure often fires once per layer or per animation frame. The dialog
      // shows the message only, so a repeat adds a duplicate line and nothing else.
      if (state.some(error => error.message === action.payload.message)) return

      action.payload.key = moment().valueOf()
      state.push(action.payload)
    },
    remove: (state, action) => {
      state.splice(action.payload.index, 1)
    },
    clear: (state) => {
      return []
    }
  }
})

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState: [],
  reducers: {
    add: (state, action) => {
      // Unieke key koppelen aan error
      action.payload.key = moment().valueOf()
      state.push(action.payload)
    },
    remove: (state, action) => {
      state.splice(action.payload.index, 1)
    },
    clear: (state) => {
      return []
    }
  }
})

export const rateLimitSlice = createSlice({
  name: "rateLimit",
  initialState: null,
  reducers: {
    // Deliberately a single object instead of a list: a rate limit is one condition,
    // not a stack of incidents, so repeated hits only refresh the reset time
    set: (state, action) => {
      return {
        ...state,
        ...action.payload,
        // Not every 429 exposes its headers, so a known reset time survives a later hit
        retryAfter: action.payload.retryAfter ?? state?.retryAfter ?? null,
        resetAt: action.payload.resetAt ?? state?.resetAt ?? null,
        key: moment().valueOf()
      }
    },
    clear: () => null
  }
})

export const { add: addError, remove: removeError, clear: clearErrors } = errorsSlice.actions
export const { add: addNotification, remove: removeNotification, clear: clearNotifications } = notificationsSlice.actions

export const { set: setRateLimit, clear: clearRateLimit } = rateLimitSlice.actions

export const errorsReducer = errorsSlice.reducer
export const notificationsReducer = notificationsSlice.reducer
export const rateLimitReducer = rateLimitSlice.reducer

// The proxy answers 429 when the anonymous request budget is used up. The status is the
// reliable signal; the text match is a fallback for callers that only pass a message along.
const rateLimitPattern = /rate[\s-]?limit|too many requests/i

export const isRateLimitError = (json, message) => {
  return json?.status === 429 || rateLimitPattern.test(message ?? "")
}

export const catchMessage = (json, functionName, successText) => (dispatch) => {
  // claude: een bewust afgebroken request (zie makeRequest) is geen fout voor de gebruiker (HSGA-204)
  if (json?.aborted) return null

  let message = "Error"
  let type = (json && json.type && json.type.toLowerCase())

  if (json) {
    if (type === "success" || successText) {
      if (successText) {
        message = successText
        type = (type ? type : "info")
      } else {
        return null
      }
    } else {
      if (json.exception) {
        message = json.exception.toString()

        if (!type) {
          type = "critical"
        }
      } else {
        return null
      }
    }
  }

  if (type === "critical") {
    if (isRateLimitError(json, message)) {
      dispatch(setRateLimit({
        message: message,
        function: functionName,
        retryAfter: json?.retryAfter ? parseInt(json.retryAfter, 10) : null,
        resetAt: json?.rateLimitReset ? Math.round(parseFloat(json.rateLimitReset) * 1000) : null
      }))
      return
    }

    // The dialog shows a summary, so keep the untruncated message reachable while developing
    console.error(`[${functionName}]`, message)

    dispatch(addError({
      message: message,
      number: json && json.exception && json.exception.number,
      level: json && json.exception && json.exception.level,
      function: functionName,
      type: type
    }))
  } else {
    dispatch(addNotification({
      message: message,
      type: type
    }))
  }
}